/**
 * Empty state shown over the map when the active workspace has no locations
 * yet. Pixel-matches the `.map-empty` card from the SpotOn Claude Design
 * mockup — the card chrome comes from {@link MAP_PANEL_SURFACE} and the
 * icon/heading/body trio from {@link MapCallout}.
 *
 * Unlike {@link MapWorkspaceOnboarding}, the search bar and toolbox stay
 * visible — the copy points at them — so this card is anchored to a corner
 * instead of centered over the map.
 */
import { cn } from '@/libs/utils/cn';
import { MapCallout } from './MapCallout';
import {
  MAP_BESIDE_CARD_LEFT_CLASS,
  MAP_INFO_CARD_CLASS,
  MAP_LAYER_PANEL_WIDTH_CLASS,
} from './mapBarGeometry';
import { MAP_PANEL_SURFACE } from './mapPanelSurface';

interface MapWorkspaceEmptyLocationsProps {
  /** A zone, deal, location or place card holds the slot: the hint moves beside it. */
  besideCard?: boolean;
}

export function MapWorkspaceEmptyLocations({
  besideCard = false,
}: MapWorkspaceEmptyLocationsProps) {
  return (
    <div
      aria-label="No locations yet"
      className={cn(
        MAP_INFO_CARD_CLASS,
        MAP_LAYER_PANEL_WIDTH_CLASS,
        'z-20 rounded-[10px] p-4',
        MAP_PANEL_SURFACE,
        besideCard && MAP_BESIDE_CARD_LEFT_CLASS,
      )}
    >
      <MapCallout
        title="No locations yet"
        icon={
          <svg viewBox="0 0 18 18" fill="none" className="size-[18px]" aria-hidden="true">
            <path
              d="M9 4v10M4 9h10"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        }
      >
        Search an address above, or hit <b className="font-bold text-[#0b2936]">New location</b> and
        drop a pin to evaluate your first site.
      </MapCallout>
    </div>
  );
}
